<script setup>
import { useUserStore } from "@/store/user-store";
import Sectionheader from "./Sectionheader.vue";
import { ref, shallowRef, watch, watchEffect } from "vue";
import AnButton from "../common/AnButton.vue";

const store = useUserStore();
const listItems = shallowRef(null);
const subtabs = ref(null);
const tabContentRef = ref(null);

const props = defineProps({
  data: { type: Object, default: {} },
});

watchEffect(() => {
  listItems.value = props.data.listData;
  // Check if the current userTab exists in listData before accessing tabs
  const currentTabData = props.data.listData[store.userTab];
  subtabs.value = currentTabData?.tabs && [
    ...currentTabData.tabs,
  ];
});

const handleTabChange = (tab) => {
  subtabs.value = subtabs.value.map((item) => ({
    ...item,
    selected: item.id === tab,
  }));
};

watch(subtabs, () => {
  tabContentRef.value?.scrollTo({ top: 0, behavior: "smooth" });
});
</script>

<template>
  <div
    ref="tabContentRef"
    class="w-100 z-1 section-wrapper p-20 d-flex flex-direction-column position-relative bg-white flex-1"
  >
    <!-- Profile Check Loading State -->
    <div v-if="store.isProfileCheckLoading" class="loading-view">
      <i class="pi pi-spin pi-spinner loading-icon"></i>
      <h3>Setting up your profile...</h3>
      <p>Please wait while we prepare your dashboard</p>
    </div>

    <!-- Normal Content -->
    <template v-else>
      <template class="desktop">
        <Sectionheader ref="sectionHeaderRef">
          <template #actions>
            <slot name="header-actions"></slot>
          </template>
        </Sectionheader>
      </template>
      <div class="d-flex flex-direction-column">
        <section
          v-if="listItems && listItems[store.userTab] && listItems[store.userTab].tabs?.length > 0"
          class="d-flex gap-10 mt-25"
        >
          <div v-for="item in subtabs" :key="item.id">
            <AnButton
              :label="item.label"
              :variant="item.selected ? 'primary' : 'tab'"
              @click="() => handleTabChange(item.id)"
            />
          </div>
        </section>
      </div>
      <!-- Debug view removed -->
      <component
        v-if="listItems && listItems[store.userTab] && !listItems[store.userTab].disabled"
        :is="listItems[store.userTab].component"
        v-bind="listItems[store.userTab]?.tabs ? { tabs: subtabs } : {}"
      />
      <div v-else-if="listItems && listItems[store.userTab] && listItems[store.userTab].disabled" class="disabled-view">
        <i class="pi pi-lock disabled-icon"></i>
        <h3>This Feature is Currently Locked</h3>
        <p>Please complete your payment to unlock this feature.</p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.section-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 20px;
  height: calc(100vh - 40px);
  /* Custom scrollbar styling to match the desired design */
  scrollbar-width: thin;
  scrollbar-color: #ced4da transparent;
}

.section-wrapper::-webkit-scrollbar {
  width: 8px;
}

.section-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.section-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #e0e0e0, #c0c0c0);
  border-radius: 10px;
  border: 2px solid transparent;
}

.section-wrapper::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #c0c0c0, #a0a0a0);
}

.debug-info {
  padding: 20px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  margin-top: 20px;
}

.debug-info p {
  margin: 8px 0;
  color: #856404;
  font-family: monospace;
}

.disabled-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  min-height: 400px;
  text-align: center;
}

.disabled-icon {
  font-size: 4rem;
  color: #9ca3af;
}

.disabled-view h3 {
  font-size: 1.5rem;
  color: #4b5563;
  margin: 0;
}

.disabled-view p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 400px;
}

.loading-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  min-height: 400px;
  text-align: center;
}

.loading-icon {
  font-size: 3rem;
  color: #155724;
}

.loading-view h3 {
  font-size: 1.5rem;
  color: #155724;
  margin: 0;
}

.loading-view p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 400px;
}

@media (max-width: 1200px) {
  .section-wrapper {
    border-radius: 0;
  }
}
@media (max-width: 768px) {
}
</style>
